import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import { Link } from 'react-router-dom';
import { useAuth, getAuthToken } from '../contexts/AuthContext';
import { AUTHORIZED_ADMIN_EMAILS, DEFAULT_ROLE_MAPPINGS } from '../config/rolePermissions';
import { extractAllUserProfilesFromEntries, isIndividualActiveUserEmail, TERMINATED_EMPLOYEE_EMAILS, getUserDisplayName, resolveUserRoleProfile } from '../utils/roleUtils';
import { EmailComboboxInput } from '../components/EmailComboboxInput';
import { loadInitialData, clearCache, getHealth, type HealthResult } from '../api/client';
import type { DirectoryEntry } from '../types/api';
import type { UserRole } from '../types/roles';
import { APP_VERSION } from '../config/version';
import { ensureActiveSheetTabs } from '../utils/tabUtils';
import { matchesSearch } from '../utils/searchUtils';
import printableDocsData from '../data/printableDocuments.json';
import {
  saveGlobalAdminSettings,
  syncAdminSettingsWithServer,
  onAdminSettingsChange,
  getLocalAdminSettings,
  deleteGlobalTeam,
  type PrintableDocGroup,
  savePrintableDocGroup,
  deletePrintableDocGroup,
  archivePrintableDocGroup,
} from '../utils/adminSync';
import { UsageAnalyticsTab } from '../components/admin/UsageAnalyticsTab';

// List of standard teams for visual multi-select
const PRESET_TEAMS: string[] = [];

export interface PrintableDocItem {
  id: string;
  docNumber: string;
  category: string;
  title: string;
  fullName: string;
  pdfUrl: string;
  docUrl: string;
}

const LEGACY_DOC_ID_ALIASES: Record<string, string> = {
  'print-doc-94': 'print-doc-128',
  'print-doc-95': 'print-doc-127',
  'print-doc-96': 'print-doc-126',
  'print-doc-97': 'print-doc-125',
  'print-doc-99': 'print-doc-124',
};

const ALL_PRINTABLE_DOCS: PrintableDocItem[] = (() => {
  const seenTitles = new Set<string>();
  const seenIds = new Set<string>();
  const items: PrintableDocItem[] = [];

  for (const [idx, doc] of (printableDocsData as any[]).entries()) {
    const title = (doc.title || doc.fullName || 'Clinical Form').trim();
    if (!title || title.toLowerCase() === 'document #' || (!doc.pdfUrl && !doc.docUrl)) {
      continue;
    }
    const id = doc.id || ('print-doc-' + (idx + 1));
    const normTitle = title.toLowerCase();
    if (seenTitles.has(normTitle) || seenIds.has(id)) {
      continue;
    }
    seenTitles.add(normTitle);
    seenIds.add(id);

    items.push({
      id,
      docNumber: doc.docNumber || ('' + (idx + 1)),
      category: doc.category || 'General',
      title,
      fullName: (doc.fullName || doc.title || 'Clinical Form').trim(),
      pdfUrl: doc.pdfUrl || '',
      docUrl: doc.docUrl || '',
    });
  }
  return items;
})();

export default function AdminPage() {
  const auth = useAuth();
  const realUserEmail = (auth.realUser?.email || auth.user?.email || '').toLowerCase().trim();

  // Access Control Check: Must be in AUTHORIZED_ADMIN_EMAILS
  const isAuthorized = AUTHORIZED_ADMIN_EMAILS.some(
    email => email.toLowerCase() === realUserEmail
  );

  const [activeTab, setActiveTab] = useLocalStorageState<'resources' | 'users' | 'team-members' | 'printable-docs' | 'usage' | 'system'>('admin-activeTab', 'resources');
  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useLocalStorageState('admin-searchQuery', '');
  const [darkMode, setDarkMode] = useLocalStorageState('admin-darkMode', true);
  const [healthStatus, setHealthStatus] = useState<HealthResult | null>(null);

  // Selected resource for editing access modal
  const [editingResource, setEditingResource] = useState<DirectoryEntry | null>(null);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [newTeamInput, setNewTeamInput] = useState('');
  const [newEmailInput, setNewEmailInput] = useState('');

  // Team Sheet Manager State
  const [selectedTeamForManager, setSelectedTeamForManager] = useLocalStorageState<string>('admin-selectedTeamForManager', 'Front Desk');
  const [teamSheetSearch, setTeamSheetSearch] = useLocalStorageState('admin-teamSheetSearch', '');
  const [teamManagerFilterMode, setTeamManagerFilterMode] = useLocalStorageState<'all' | 'assigned' | 'unassigned'>('admin-teamManagerFilterMode', 'assigned');
  const [userSearch, setUserSearch] = useLocalStorageState('admin-userSearch', '');
  const [teamTabFilter, setTeamTabFilter] = useLocalStorageState<'active' | 'archived' | 'all'>('admin-teamTabFilter', 'active');

  const [customTeams, setCustomTeams] = useState<string[]>(() => {
    try {
      const { customTeams, sheetCustomTeams, deletedTeams = [] } = getLocalAdminSettings();
      const deletedSet = new Set(deletedTeams.map(t => t.toLowerCase().trim()));
      let merged = [...(customTeams || [])];
      if (sheetCustomTeams && sheetCustomTeams.length > 0) {
        merged = [...merged, ...sheetCustomTeams];
      }
      if (merged.length > 0) {
        return Array.from(new Set(merged)).filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim()));
      }
    } catch {}
    return PRESET_TEAMS;
  });

  const [archivedTeams, setArchivedTeams] = useState<string[]>(() => {
    try {
      const { archivedTeams } = getLocalAdminSettings();
      if (archivedTeams && archivedTeams.length > 0) {
        return archivedTeams.filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team');
      }
    } catch {}
    return [];
  });

  // Modal states for Team Directory & Management
  const [selectedTeamForManage, setSelectedTeamForManage] = useState<string | null>(null);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [newCreateTeamName, setNewCreateTeamName] = useState('');
  const [newCreateTeamMembers, setNewCreateTeamMembers] = useState<string[]>([]);
  const [teamToRename, setTeamToRename] = useState<string | null>(null);
  const [newRenameTeamInput, setNewRenameTeamInput] = useState('');
  const [rosterSearchQuery, setRosterSearchQuery] = useState('');

  // customSheetTitles state has been removed as the sheet name is now directly driven by the Master Index.

  const [editingSheetTitleGroup, setEditingSheetTitleGroup] = useState<{
    rawDocKey: string;
    originalSheetName: string;
    currentCustomTitle: string;
  } | null>(null);
  const [newCustomSheetTitleInput, setNewCustomSheetTitleInput] = useState('');

  const handleSaveSheetRename = async (rawDocKey: string, newTitle: string) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    
    // In the flat list, rawDocKey is entry.id which is `${ssId}-${tabName}`
    const entry = entries.find(e => e.id === rawDocKey);
    if (!entry) return;

    try {
      const token = getAuthToken();
      if (!token) throw new Error("Not authenticated");

      const response = await fetch('/api/update-index-name', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ssId: entry.ssId,
          tabName: entry.tabName,
          newName: trimmed
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to update name");
      }

      // Update local entries state so UI reflects the new name
      setEntries(prev =>
        prev.map(e => {
          if (e.id === rawDocKey) {
            return { ...e, name: trimmed };
          }
          return e;
        })
      );

      setEditingSheetTitleGroup(null);
    } catch (error: any) {
      console.error("Failed to rename sheet:", error);
      alert("Error saving rename: " + error.message);
    }
  };

  const handleResetSheetRename = () => {
    // Resetting can just put the old tabName or something. Or we just do nothing since we don't have a default name column.
    setEditingSheetTitleGroup(null);
  };

  const handleRemoveSheet = async (rawDocKey: string) => {
    if (!window.confirm("Are you sure you want to remove this sheet from the Intranet? You will need to re-add it from the Google Sheet 'Tab List' if you change your mind.")) {
      return;
    }

    const entry = entries.find(e => e.id === rawDocKey);
    if (!entry) return;

    try {
      const token = getAuthToken();
      if (!token) throw new Error("Not authenticated");

      const response = await fetch('/api/toggle-index-visibility', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ssId: entry.ssId,
          tabName: entry.tabName,
          visible: false
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to remove sheet");
      }

      // Update local entries state to remove the sheet
      setEntries(prev => prev.filter(e => e.id !== rawDocKey));
    } catch (error: any) {
      console.error("Failed to remove sheet:", error);
      alert("Error removing sheet: " + error.message);
    }
  };



  // Whenever active team in Team Sheet Manager changes, default filter mode to 'assigned'
  useEffect(() => {
    setTeamManagerFilterMode('assigned');
  }, [selectedTeamForManager]);

  // User role mappings state
  const [userMappings, setUserMappings] = useState<Record<string, { roles: UserRole[]; primaryTeam: string; assignedTeams: string[] }>>(() => {
    try {
      const saved = localStorage.getItem('ssd_admin_user_mappings');
      const parsed = saved ? JSON.parse(saved) : DEFAULT_ROLE_MAPPINGS;
      const cleaned: Record<string, any> = {};
      Object.entries(parsed).forEach(([email, val]) => {
        if (isIndividualActiveUserEmail(email)) {
          cleaned[email] = val;
        }
      });
      return cleaned;
    } catch {
      return DEFAULT_ROLE_MAPPINGS;
    }
  });

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('Staff');
  const [newUserTeam, setNewUserTeam] = useState('Front Desk');
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  // =========================================================================
  // PRINTABLE DOCUMENT GROUPS STATE & HANDLERS
  // =========================================================================
  const [docGroups, setDocGroups] = useState<PrintableDocGroup[]>(() => {
    try {
      const { printableDocGroups } = getLocalAdminSettings();
      return Array.isArray(printableDocGroups) ? printableDocGroups : [];
    } catch {
      return [];
    }
  });

  const [selectedDocGroupId, setSelectedDocGroupId] = useState<string | null>(null);
  const [docGroupTabFilter, setDocGroupTabFilter] = useLocalStorageState<'active' | 'archived' | 'all'>('admin-docGroupTabFilter', 'active');
  const [docGroupSearch, setDocGroupSearch] = useState('');
  const [docLibrarySearch, setDocLibrarySearch] = useState('');
  const [docLibraryCategory, setDocLibraryCategory] = useState<string>('All');

  // Modals
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupNameInput, setNewGroupNameInput] = useState('');
  const [groupToRename, setGroupToRename] = useState<PrintableDocGroup | null>(null);
  const [renameGroupInput, setRenameGroupInput] = useState('');

  // Drag and drop ordering within group
  const [draggedGroupDocId, setDraggedGroupDocId] = useState<string | null>(null);
  const [dragOverGroupDocId, setDragOverGroupDocId] = useState<string | null>(null);

  // Load resources & system status
  useEffect(() => {
    if (!isAuthorized) return;

    async function fetchData() {
      // Step 1: Render directory entries immediately (uses instant cache if available)
      try {
        const initial = await loadInitialData();
        if (initial.directory.success && initial.directory.entries.length > 0) {
          setEntries(ensureActiveSheetTabs(initial.directory.entries));
        }
      } catch (err) {
        console.error('[Admin] Error loading admin data:', err);
      } finally {
        setLoading(false);
      }

      // Step 2: Query health status in background without blocking UI render
      try {
        const health = await getHealth();
        setHealthStatus(health);
      } catch (err) {
        console.error('[Admin] Error loading health status:', err);
      }
    }

    syncAdminSettingsWithServer().then(() => {
      fetchData();
    });

    const interval = setInterval(() => {
      syncAdminSettingsWithServer().then(updated => {
        if (updated) fetchData();
      });
    }, 20000);

    const unsubscribe = onAdminSettingsChange(() => {
      fetchData();
      const { customTeams, sheetCustomTeams, deletedTeams = [], printableDocGroups = [] } = getLocalAdminSettings();
      const deletedSet = new Set(deletedTeams.map(t => t.toLowerCase().trim()));
      let merged = [...(customTeams || [])];
      if (sheetCustomTeams && sheetCustomTeams.length > 0) {
        merged = [...merged, ...sheetCustomTeams];
      }
      if (merged.length > 0) {
        setCustomTeams(Array.from(new Set(merged)).filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())));
      }
      setDocGroups(printableDocGroups || []);
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [isAuthorized]);

  // Dynamically extract all unique teams from customTeams + loaded database entries
  const allAvailableTeams = useMemo(() => {
    const { deletedTeams = [] } = getLocalAdminSettings();
    const deletedSet = new Set(deletedTeams.map(t => t.toLowerCase().trim()));
    const teamSet = new Set<string>();

    customTeams.forEach(t => {
      if (t && t.trim() !== '' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())) {
        teamSet.add(t.trim());
      }
    });

    entries.forEach(e => {
      if (e.team) {
        e.team.split(',').map(t => t.trim()).filter(Boolean).forEach(t => {
          if (t && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim())) {
            teamSet.add(t);
          }
        });
      }
    });

    return Array.from(teamSet).sort((a, b) => a.localeCompare(b));
  }, [entries, customTeams]);

  // Extract all active user profiles dynamically from sheet entries & user mappings
  const activeUserProfiles = useMemo(() => {
    return extractAllUserProfilesFromEntries(entries, userMappings);
  }, [entries, userMappings]);

  // Group active user profiles by team for Tab 4: Team Directory
  const teamDirectoryMap = useMemo(() => {
    const map = new Map<string, Array<{ email: string; team: string; roles: UserRole[]; assignedTeams: string[] }>>();

    activeUserProfiles.forEach(user => {
      const teams = user.assignedTeams && user.assignedTeams.length > 0 
        ? user.assignedTeams.filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'no team') 
        : [];
      teams.forEach(team => {
        if (!map.has(team)) {
          map.set(team, []);
        }
        map.get(team)!.push(user);
      });
    });

    return map;
  }, [activeUserProfiles]);

  // Persist custom resource permission overrides to localStorage whenever entries state changes
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!loading && entries.length > 0) {
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        return;
      }
      try {
        const overrides: Record<string, { team: string; accessEmails: string[]; categories: string[] }> = {};
        entries.forEach(item => {
          overrides[item.id] = {
            team: item.team,
            categories: item.categories,
            accessEmails: item.accessEmails,
          };
          overrides[item.name] = {
            team: item.team,
            categories: item.categories,
            accessEmails: item.accessEmails,
          };
        });
        localStorage.setItem('ssd_admin_custom_resource_permissions', JSON.stringify(overrides));
        console.log('[Admin] Saved custom resource permissions persistently to localStorage');
      } catch (err) {
        console.error('[Admin] Error saving custom permissions:', err);
      }
    }
  }, [entries, loading]);

  // Persist user mappings to global server storage is now handled directly inside the setter functions to prevent overwrite on load.



  // Toggle Team Selection in Modal
  const toggleTeamSelection = (teamName: string) => {
    setSelectedTeams(prev =>
      prev.includes(teamName) ? prev.filter(t => t !== teamName) : [...prev, teamName]
    );
  };

  // Add Custom Team in Modal
  const handleAddCustomTeam = () => {
    if (!newTeamInput.trim()) return;
    const team = newTeamInput.trim();
    if (!selectedTeams.includes(team)) {
      setSelectedTeams(prev => [...prev, team]);
    }
    setNewTeamInput('');
  };

  // Toggle Email Selection in Modal
  const toggleEmailSelection = (email: string) => {
    const normalized = email.toLowerCase().trim();
    setSelectedEmails(prev =>
      prev.includes(normalized) ? prev.filter(e => e !== normalized) : [...prev, normalized]
    );
  };

  // Add Custom Email in Modal
  const handleAddCustomEmail = () => {
    if (!newEmailInput.trim()) return;
    const email = newEmailInput.trim().toLowerCase();
    if (!selectedEmails.includes(email)) {
      setSelectedEmails(prev => [...prev, email]);
    }
    setNewEmailInput('');
  };

  // Helper to persist entries overrides to localStorage immediately
  const persistEntriesOverrides = useCallback((updatedEntries: DirectoryEntry[]) => {
    if (updatedEntries.length === 0) return;
    try {
      const overrides: Record<string, { team: string; accessEmails: string[]; categories: string[] }> = {};
      updatedEntries.forEach(item => {
        const rawTeam = (item.team || '').trim();
        const cleanTeam = (rawTeam.toLowerCase() === 'unassigned' || rawTeam.toLowerCase() === 'no team' || rawTeam.toLowerCase() === 'management') ? '' : rawTeam;

        const itemData = {
          team: cleanTeam,
          categories: item.categories,
          accessEmails: item.accessEmails,
        };
        overrides[item.id] = itemData;
        overrides[item.name] = itemData;
      });
      saveGlobalAdminSettings({ resourcePermissions: overrides });
      console.log('[Admin] Persisted custom resource permissions globally:', Object.keys(overrides).length);
    } catch (err) {
      console.error('[Admin] Error persisting overrides:', err);
    }
  }, []);

  // Save Resource Access Changes
  const handleSaveResourceAccess = () => {
    if (!editingResource) return;

    const updatedTeamString = selectedTeams.join(', ');

    setEntries(prev => {
      const updated = prev.map(item => {
        if (item.id === editingResource.id) {
          return {
            ...item,
            team: updatedTeamString,
            accessEmails: selectedEmails,
          };
        }
        return item;
      });
      persistEntriesOverrides(updated);
      return updated;
    });

    setEditingResource(null);
  };

  // Toggle Sheet Membership in Team Sheet Manager
  const toggleSheetForSelectedTeam = (resourceId: string) => {
    setEntries(prev => {
      const updated = prev.map(item => {
        if (item.id === resourceId) {
          const currentTeams = item.team ? item.team.split(',').map(t => t.trim()).filter(Boolean) : [];
          const hasTeam = currentTeams.includes(selectedTeamForManager);
          const newTeams = hasTeam
            ? currentTeams.filter(t => t !== selectedTeamForManager)
            : [...currentTeams, selectedTeamForManager];
          return {
            ...item,
            team: newTeams.join(', '),
          };
        }
        return item;
      });
      persistEntriesOverrides(updated);
      return updated;
    });
  };

  // Bulk Select / Deselect All for Team (Scope strictly to items currently matching search filter)
  const handleBulkTeamAssignment = (assignAll: boolean) => {
    const targetIds = new Set(teamManagerFilteredEntries.map(e => e.id));

    setEntries(prev => {
      const updated = prev.map(item => {
        if (!targetIds.has(item.id)) return item; // Skip items not in active search view!

        const currentTeams = item.team ? item.team.split(',').map(t => t.trim()).filter(Boolean) : [];
        const hasTeam = currentTeams.includes(selectedTeamForManager);

        if (assignAll && !hasTeam) {
          return { ...item, team: [...currentTeams, selectedTeamForManager].join(', ') };
        } else if (!assignAll && hasTeam) {
          return { ...item, team: currentTeams.filter(t => t !== selectedTeamForManager).join(', ') };
        }
        return item;
      });
      persistEntriesOverrides(updated);
      return updated;
    });
  };



  // Add New User Mapping
  const handleAddUserMapping = () => {
    if (!newUserEmail.trim()) return;
    const email = newUserEmail.trim().toLowerCase();

    setUserMappings(prev => {
      const updated = {
        ...prev,
        [email]: {
          roles: [newUserRole],
          primaryTeam: newUserTeam,
          assignedTeams: [newUserTeam],
        },
      };
      saveGlobalAdminSettings({ userMappings: updated });
      return updated;
    });

    setNewUserEmail('');
    setShowAddUserModal(false);
  };

  // Update Existing User Role
  const handleUpdateUserRole = (email: string, role: UserRole) => {
    setUserMappings(prev => {
      const existing = prev[email] || { roles: ['Staff'], primaryTeam: '', assignedTeams: [] };
      const updated = {
        ...prev,
        [email]: {
          ...existing,
          roles: [role],
          assignedTeams: existing.assignedTeams || (existing.primaryTeam ? [existing.primaryTeam] : []),
        },
      };
      saveGlobalAdminSettings({ userMappings: updated });
      return updated;
    });
  };

  const handleToggleUserTeam = (email: string, team: string, currentAssignedTeams?: string[]) => {
    const normEmail = email.toLowerCase().trim();
    const existing = userMappings[normEmail] || userMappings[email];
    
    let baseTeams: string[] = [];
    if (Array.isArray(currentAssignedTeams)) {
      baseTeams = currentAssignedTeams;
    } else if (existing && Array.isArray(existing.assignedTeams)) {
      baseTeams = existing.assignedTeams;
    } else {
      const profile = resolveUserRoleProfile(normEmail);
      baseTeams = profile.assignedTeams || [];
    }

    const currentAssigned = Array.isArray(baseTeams) 
      ? baseTeams.filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management') 
      : [];

    const newAssigned = currentAssigned.includes(team)
      ? currentAssigned.filter(t => t !== team)
      : [...currentAssigned, team];

    const cleanedAssigned = newAssigned.filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management');

    // Update activeEmployees in local state immediately so UI updates instantly
    const localSettings = getLocalAdminSettings();
    const updatedActiveEmployees = localSettings.activeEmployees ? [...localSettings.activeEmployees] : [];
    const empIdx = updatedActiveEmployees.findIndex(e => e.email.toLowerCase() === normEmail);
    if (empIdx !== -1) {
      updatedActiveEmployees[empIdx] = {
        ...updatedActiveEmployees[empIdx],
        team: cleanedAssigned.join(', ')
      };
    }

    const updated = {
      ...userMappings,
      [normEmail]: {
        roles: existing?.roles || ['Staff'],
        primaryTeam: cleanedAssigned.length > 0 ? cleanedAssigned[0] : '',
        assignedTeams: cleanedAssigned,
      },
    };

    setUserMappings(updated);
    saveGlobalAdminSettings({ 
      userMappings: updated,
      activeEmployees: updatedActiveEmployees
    });

    // Async sync to Google Sheets Employee tab
    try {
      const token = getAuthToken();
      if (token) {
        fetch('/api/update-employee-teams', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ email: normEmail, teams: cleanedAssigned })
        }).catch(e => console.warn("Failed to update employee teams to sheet:", e));
      }
    } catch (err) {
      console.warn("Failed to trigger employee team update:", err);
    }
  };

  // Helper to create a new team
  const handleCreateTeam = async () => {
    const name = newCreateTeamName.trim();
    if (!name) return;

    // If this team was previously in deletedTeams, un-delete it
    const { deletedTeams = [] } = getLocalAdminSettings();
    const nameLower = name.toLowerCase();
    if (deletedTeams.some(t => t.toLowerCase().trim() === nameLower)) {
      const updatedDeleted = deletedTeams.filter(t => t.toLowerCase().trim() !== nameLower);
      saveGlobalAdminSettings({ deletedTeams: updatedDeleted });
    }

    if (!customTeams.some(t => t.toLowerCase() === nameLower)) {
      const updated = [...customTeams, name];
      setCustomTeams(updated);
      saveGlobalAdminSettings({ customTeams: updated });
      
      // Async append to the Google Sheet Dropdowns tab
      try {
        const token = getAuthToken();
        if (token) {
          fetch('/api/append-team', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ teamName: name })
          }).catch(e => console.error("Failed to append team to sheet:", e));
        }
      } catch (err) {
        console.error("Failed to trigger team append:", err);
      }
    }

    // Assign initial members if selected
    if (newCreateTeamMembers.length > 0) {
      newCreateTeamMembers.forEach(email => {
        handleToggleUserTeam(email, name);
      });
    }

    setNewCreateTeamName('');
    setNewCreateTeamMembers([]);
    setShowCreateTeamModal(false);
  };

  // Helper to rename a team across user mappings and entries
  const handleRenameTeam = () => {
    if (!teamToRename) return;
    const oldName = teamToRename;
    const newName = newRenameTeamInput.trim();
    if (!newName || newName === oldName) {
      setTeamToRename(null);
      return;
    }

    // 1. Update customTeams
    const updatedCustom = customTeams.map(t => t === oldName ? newName : t);
    setCustomTeams(updatedCustom);
    saveGlobalAdminSettings({ customTeams: updatedCustom });

    // 2. Update archivedTeams
    if (archivedTeams.includes(oldName)) {
      const updatedArchived = archivedTeams.map(t => t === oldName ? newName : t);
      setArchivedTeams(updatedArchived);
      saveGlobalAdminSettings({ archivedTeams: updatedArchived });
    }

    // 3. Update userMappings
    setUserMappings(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(email => {
        const u = updated[email];
        if (u) {
          const assigned = u.assignedTeams || [];
          if (assigned.includes(oldName)) {
            const newAssigned = assigned.map(t => t === oldName ? newName : t);
            updated[email] = {
              ...u,
              primaryTeam: u.primaryTeam === oldName ? newName : u.primaryTeam,
              assignedTeams: newAssigned,
            };
          }
        }
      });
      saveGlobalAdminSettings({ userMappings: updated });
      return updated;
    });

    // 4. Update entries
    setEntries(prev => {
      return prev.map(e => {
        if (!e.team) return e;
        const teams = e.team.split(',').map(t => t.trim());
        if (teams.includes(oldName)) {
          const newTeamStr = teams.map(t => t === oldName ? newName : t).join(', ');
          return { ...e, team: newTeamStr };
        }
        return e;
      });
    });

    if (selectedTeamForManage === oldName) {
      setSelectedTeamForManage(newName);
    }
    setTeamToRename(null);
    setNewRenameTeamInput('');
  };

  // Helper to archive/unarchive a team
  const handleToggleArchiveTeam = (teamName: string) => {
    setArchivedTeams(prev => {
      const updated = prev.includes(teamName)
        ? prev.filter(t => t !== teamName)
        : [...prev, teamName];
      saveGlobalAdminSettings({ archivedTeams: updated });
      return updated;
    });
  };

  // Helper to delete a team
  const handleDeleteTeam = async (teamName: string) => {
    if (!teamName) return;
    if (!window.confirm(`Are you sure you want to delete team "${teamName}"? Members will be unassigned from this team.`)) {
      return;
    }

    const targetLower = teamName.toLowerCase().trim();

    // 1. Immediately update local react state
    setCustomTeams(prev => prev.filter(t => t.toLowerCase().trim() !== targetLower));
    setArchivedTeams(prev => prev.filter(t => t.toLowerCase().trim() !== targetLower));

    // 2. Strip from in-memory entries state so allAvailableTeams memo and tables update instantly
    setEntries(prev => prev.map(e => {
      if (!e.team) return e;
      const remaining = e.team.split(',').map(t => t.trim()).filter(t => t.toLowerCase().trim() !== targetLower);
      return {
        ...e,
        team: remaining.join(', '),
      };
    }));

    // 3. Clear selected modals/filters if currently set to this team
    if (selectedTeamForManage && selectedTeamForManage.toLowerCase().trim() === targetLower) {
      setSelectedTeamForManage(null);
    }
    if (selectedTeamForManager && selectedTeamForManager.toLowerCase().trim() === targetLower) {
      setSelectedTeamForManager('All');
    }

    // 4. Execute atomic global deletion across all storages & backend
    await deleteGlobalTeam(teamName);
  };





  // Total assigned count for selected team across all 302 entries
  const totalAssignedForSelectedTeam = useMemo(() => {
    return entries.filter(e => {
      const teams = e.team ? e.team.split(',').map(t => t.trim()) : [];
      return teams.includes(selectedTeamForManager);
    }).length;
  }, [entries, selectedTeamForManager]);

  // Filtered resources for Tab 2 (Team Sheet Manager)
  const teamManagerFilteredEntries = useMemo(() => {
    return entries.filter(e => {
      const teams = e.team ? e.team.split(',').map(t => t.trim()) : [];
      const isAssigned = teams.includes(selectedTeamForManager);

      if (teamManagerFilterMode === 'assigned' && !isAssigned) {
        return false;
      }
      if (teamManagerFilterMode === 'unassigned' && isAssigned) {
        return false;
      }

      if (teamSheetSearch.trim()) {
        const matched = matchesSearch(teamSheetSearch, {
          primary: e.name,
          secondary: [e.description, e.tabName],
          tertiary: [e.team, ...(e.categories || [])]
        });
        if (!matched) {
          return false;
        }
      }

      return true;
    });
  }, [entries, teamSheetSearch, teamManagerFilterMode, selectedTeamForManager]);

  // Filtered printable doc groups
  const filteredDocGroups = useMemo(() => {
    return docGroups.filter(g => {
      if (docGroupTabFilter === 'active' && g.archived) return false;
      if (docGroupTabFilter === 'archived' && !g.archived) return false;
      if (docGroupSearch.trim()) {
        return matchesSearch(docGroupSearch, { primary: g.name });
      }
      return true;
    });
  }, [docGroups, docGroupTabFilter, docGroupSearch]);

  // Active selected group
  const activeSelectedGroup = useMemo(() => {
    if (selectedDocGroupId) {
      const match = docGroups.find(g => g.id === selectedDocGroupId);
      if (match) return match;
    }
    if (filteredDocGroups.length > 0) return filteredDocGroups[0];
    return null;
  }, [docGroups, selectedDocGroupId, filteredDocGroups]);

  // Assigned printable docs in the selected group (in their exact custom order)
  const assignedDocsInSelectedGroup = useMemo(() => {
    if (!activeSelectedGroup || !Array.isArray(activeSelectedGroup.documentIds)) return [];
    const docMap = new Map<string, PrintableDocItem>();
    ALL_PRINTABLE_DOCS.forEach(doc => {
      docMap.set(doc.id, doc);
    });
    return activeSelectedGroup.documentIds
      .map(id => {
        const resolvedId = LEGACY_DOC_ID_ALIASES[id] || id;
        return docMap.get(resolvedId) || docMap.get(id);
      })
      .filter((doc): doc is PrintableDocItem => Boolean(doc));
  }, [activeSelectedGroup]);

  // Categories in printable library
  const docCategories = useMemo(() => {
    const cats = new Set<string>();
    ALL_PRINTABLE_DOCS.forEach(d => {
      if (d.category) cats.add(d.category);
    });
    return ['All', ...Array.from(cats).sort()];
  }, []);

  // Filtered library docs
  const filteredLibraryDocs = useMemo(() => {
    return ALL_PRINTABLE_DOCS.filter(doc => {
      if (docLibraryCategory !== 'All' && doc.category !== docLibraryCategory) return false;
      if (docLibrarySearch.trim()) {
        return matchesSearch(docLibrarySearch, {
          primary: doc.title,
          secondary: [doc.fullName, doc.docNumber, doc.id],
          tertiary: doc.category
        });
      }
      return true;
    });
  }, [docLibraryCategory, docLibrarySearch]);

  // Group Handlers
  const handleCreateDocGroup = async () => {
    const trimmed = newGroupNameInput.trim();
    if (!trimmed) return;
    const newGroup: PrintableDocGroup = {
      id: `doc-grp-${Date.now()}`,
      name: trimmed,
      archived: false,
      documentIds: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    const next = [...docGroups, newGroup];
    setDocGroups(next);
    setSelectedDocGroupId(newGroup.id);
    setNewGroupNameInput('');
    setShowCreateGroupModal(false);
    await savePrintableDocGroup(newGroup);
  };

  const handleSaveRenameGroup = async () => {
    if (!groupToRename) return;
    const trimmed = renameGroupInput.trim();
    if (!trimmed) return;
    const updated = { ...groupToRename, name: trimmed, updatedAt: Date.now() };
    const next = docGroups.map(g => g.id === groupToRename.id ? updated : g);
    setDocGroups(next);
    setGroupToRename(null);
    setRenameGroupInput('');
    await savePrintableDocGroup(updated);
  };

  const handleToggleArchiveGroup = async (group: PrintableDocGroup) => {
    const newArchived = !group.archived;
    const updated = { ...group, archived: newArchived, updatedAt: Date.now() };
    const next = docGroups.map(g => g.id === group.id ? updated : g);
    setDocGroups(next);
    await archivePrintableDocGroup(group.id, newArchived);
  };

  const handleDeleteGroup = async (group: PrintableDocGroup) => {
    if (!window.confirm(`Are you sure you want to permanently delete the printable document group "${group.name}"?`)) {
      return;
    }
    const next = docGroups.filter(g => g.id !== group.id);
    setDocGroups(next);
    if (selectedDocGroupId === group.id) {
      setSelectedDocGroupId(next.length > 0 ? next[0].id : null);
    }
    await deletePrintableDocGroup(group.id);
  };

  const handleAddDocToGroup = async (docId: string) => {
    if (!activeSelectedGroup) return;
    const isDoc = (id: string) => (LEGACY_DOC_ID_ALIASES[id] || id) === (LEGACY_DOC_ID_ALIASES[docId] || docId) || id === docId;
    if (activeSelectedGroup.documentIds.some(isDoc)) return;
    const updatedDocIds = [...activeSelectedGroup.documentIds, docId];
    const updated = { ...activeSelectedGroup, documentIds: updatedDocIds, updatedAt: Date.now() };
    const next = docGroups.map(g => g.id === activeSelectedGroup.id ? updated : g);
    setDocGroups(next);
    await savePrintableDocGroup(updated);
  };

  const handleRemoveDocFromGroup = async (docId: string) => {
    if (!activeSelectedGroup) return;
    const isDoc = (id: string) => (LEGACY_DOC_ID_ALIASES[id] || id) === (LEGACY_DOC_ID_ALIASES[docId] || docId) || id === docId;
    const updatedDocIds = activeSelectedGroup.documentIds.filter(id => !isDoc(id));
    const updated = { ...activeSelectedGroup, documentIds: updatedDocIds, updatedAt: Date.now() };
    const next = docGroups.map(g => g.id === activeSelectedGroup.id ? updated : g);
    setDocGroups(next);
    await savePrintableDocGroup(updated);
  };

  const handleMoveDocInGroup = async (index: number, direction: 'up' | 'down') => {
    if (!activeSelectedGroup) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= activeSelectedGroup.documentIds.length) return;

    const updatedDocIds = [...activeSelectedGroup.documentIds];
    const [removed] = updatedDocIds.splice(index, 1);
    updatedDocIds.splice(targetIndex, 0, removed);

    const updated = { ...activeSelectedGroup, documentIds: updatedDocIds, updatedAt: Date.now() };
    const next = docGroups.map(g => g.id === activeSelectedGroup.id ? updated : g);
    setDocGroups(next);
    await savePrintableDocGroup(updated);
  };

  const handleDropDocInGroup = async (sourceDocId: string, targetDocId: string) => {
    if (!activeSelectedGroup || sourceDocId === targetDocId) return;
    const currentIds = [...activeSelectedGroup.documentIds];
    const sourceIndex = currentIds.indexOf(sourceDocId);
    const targetIndex = currentIds.indexOf(targetDocId);
    if (sourceIndex === -1 || targetIndex === -1) return;

    currentIds.splice(sourceIndex, 1);
    currentIds.splice(targetIndex, 0, sourceDocId);

    const updated = { ...activeSelectedGroup, documentIds: currentIds, updatedAt: Date.now() };
    const next = docGroups.map(g => g.id === activeSelectedGroup.id ? updated : g);
    setDocGroups(next);
    setDraggedGroupDocId(null);
    setDragOverGroupDocId(null);
    await savePrintableDocGroup(updated);
  };

  const handleAddAllFilteredDocs = async () => {
    if (!activeSelectedGroup) return;
    const currentSet = new Set(activeSelectedGroup.documentIds);
    const newIds = filteredLibraryDocs.map(d => d.id).filter(id => !currentSet.has(id));
    if (newIds.length === 0) return;

    const updatedDocIds = [...activeSelectedGroup.documentIds, ...newIds];
    const updated = { ...activeSelectedGroup, documentIds: updatedDocIds, updatedAt: Date.now() };
    const next = docGroups.map(g => g.id === activeSelectedGroup.id ? updated : g);
    setDocGroups(next);
    await savePrintableDocGroup(updated);
  };

  // Access Denied Screen
  if (!auth.isLoading && !isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-xl shadow-2xl p-8 border border-red-500/30 text-center">
          <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2 text-red-400">Access Restricted</h1>
          <p className="text-gray-300 text-sm mb-6">
            The Admin Control Panel is strictly restricted to authorized administrators (<span className="text-blue-400 font-mono">agonzalez@ssdspc.com</span>, <span className="text-blue-400 font-mono">admin@ssdspc.com</span>, <span className="text-blue-400 font-mono">mm@ssdspc.com</span>).
          </p>
          <p className="text-xs text-gray-500 mb-6">Current logged in user: {auth.user?.email || 'Unauthenticated'}</p>
          <Link
            to="/"
            className="inline-block px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-lg transition-colors"
          >
            Return to Intranet Portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Exit to Intranet
            </Link>
            <div className="h-6 w-px bg-gray-700"></div>
            <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
              <span className="px-2.5 py-1 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-md font-semibold">ADMIN</span>
              <span>Intranet Control Panel</span>
              <span className="px-2 py-0.5 text-xs font-mono font-bold text-amber-400 bg-amber-950/80 border border-amber-500/40 rounded shadow-sm">{APP_VERSION}</span>
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-gray-400">Logged in as Administrator</p>
              <p className="text-sm font-semibold text-blue-400">{auth.user?.email}</p>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200"
              aria-label="Toggle theme"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>

        {/* Tab Navigation Bar */}
        <div className="max-w-[1750px] mx-auto px-4 sm:px-8 flex gap-8 border-t border-gray-700/50">
          <button
            onClick={() => setActiveTab('resources')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'resources'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 4h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Resource & Team Access Manager
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 43a8 8 0 100-16 8 8 0 000 16zM21 21H3v-1a6 6 0 016-6h12a6 6 0 016 6v1z" />
            </svg>
            User Roles & Homepages
          </button>

          <button
            onClick={() => setActiveTab('team-members')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'team-members'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Team Directory (People by Team)
          </button>

          <button
            onClick={() => setActiveTab('printable-docs')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'printable-docs'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Printable Doc Groups
            {docGroups.length > 0 && (
              <span className="bg-blue-900/60 text-blue-300 text-xs px-2 py-0.5 rounded-full font-bold">
                {docGroups.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('usage')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'usage'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Usage & Analytics
          </button>

          <button
            onClick={() => setActiveTab('system')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'system'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            System Diagnostics
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-[1750px] mx-auto px-4 sm:px-8 py-8">
        {/* TAB 1: Resource & Team Access Manager (MERGED UNIFIED VIEW) */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            {/* Control Panel Header: Active Team Picker, Filter Pills, Bulk Controls & Search */}
            <div className="bg-gray-800 p-5 rounded-2xl border border-gray-700 space-y-4 shadow-xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-700 pb-4">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <span>🏢</span> Resource, Sheet & Tab Manager
                  </h2>
                  <p className="text-xs text-gray-400">
                    Unified panel to manage sheet team assignments, permissions, and categorize tabs as <strong className="text-amber-400 font-semibold">⭐ Main Sheets</strong> or <strong className="text-blue-300 font-semibold">📄 All Sheets</strong>.
                  </p>
                </div>

                {/* Active Team Picker */}
                <div className="flex items-center gap-3 w-full sm:w-auto bg-gray-900 px-3 py-1.5 rounded-xl border border-gray-700">
                  <label className="text-xs font-bold text-gray-300 flex-shrink-0">Active Team:</label>
                  <select
                    value={selectedTeamForManager}
                    onChange={e => setSelectedTeamForManager(e.target.value)}
                    className="px-3 py-1 bg-gray-800 border border-blue-500/50 text-white font-bold text-xs rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    {allAvailableTeams.map(team => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Filter Pills, Bulk Actions & Search Input */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Filter Mode Toggle Pills: Assigned Only on Left, Show All in Middle, Unassigned Only on Right */}
                  <div className="flex bg-gray-900 p-1 rounded-lg border border-gray-700">
                    <button
                      type="button"
                      onClick={() => setTeamManagerFilterMode('assigned')}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                        teamManagerFilterMode === 'assigned'
                          ? 'bg-emerald-600 text-white shadow'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Assigned Only ({totalAssignedForSelectedTeam})
                    </button>
                    <button
                      type="button"
                      onClick={() => setTeamManagerFilterMode('all')}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                        teamManagerFilterMode === 'all'
                          ? 'bg-blue-600 text-white shadow'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Show All ({entries.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setTeamManagerFilterMode('unassigned')}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                        teamManagerFilterMode === 'unassigned'
                          ? 'bg-amber-600 text-white shadow'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Unassigned Only ({entries.length - totalAssignedForSelectedTeam})
                    </button>
                  </div>

                  <div className="h-4 w-px bg-gray-700 hidden sm:block"></div>

                  {/* Bulk Select / Deselect for Active Team */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleBulkTeamAssignment(true)}
                      className="px-3 py-1 bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-500/30 text-xs font-semibold rounded transition-colors"
                      title={`Assign all displayed sheets to ${selectedTeamForManager}`}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkTeamAssignment(false)}
                      className="px-3 py-1 bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-500/30 text-xs font-semibold rounded transition-colors"
                      title={`Deselect all displayed sheets from ${selectedTeamForManager}`}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="relative w-full sm:w-72">
                  <input
                    type="text"
                    placeholder="Search sheets or tabs..."
                    value={teamSheetSearch}
                    onChange={e => setTeamSheetSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <svg className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* High-Density Unified Access Table */}
            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading intranet resources...</div>
            ) : (
              <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-gray-300">
                    <thead className="bg-gray-900 text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700">
                      <tr>
                        <th className="px-6 py-3.5 min-w-[200px]">Sheet Name</th>
                        <th className="px-6 py-3.5 min-w-[200px]">Name in Index</th>
                        <th className="px-6 py-3.5 min-w-[200px]">Access</th>
                        <th className="px-6 py-3.5 min-w-[200px]">Teams</th>
                        <th className="px-6 py-3.5 text-right min-w-[120px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/60">
                      {entries
                        .filter(entry => {
                          if (teamManagerFilterMode === 'assigned' && !entry.team.includes(selectedTeamForManager)) return false;
                          if (teamManagerFilterMode === 'unassigned' && entry.team.includes(selectedTeamForManager)) return false;

                          if (teamSheetSearch.trim()) {
                            const matched = matchesSearch(teamSheetSearch, {
                              primary: entry.name,
                              secondary: [entry.description, entry.tabName],
                              tertiary: [entry.team, ...(entry.categories || [])]
                            });
                            if (!matched) return false;
                          }
                          return true;
                        })
                        .map(entry => {
                          return (
                            <tr key={entry.id} className="hover:bg-gray-750 transition-colors">
                              <td className="px-6 py-4">
                                <a
                                  href={entry.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-bold text-white hover:text-blue-400 underline decoration-blue-500/50 hover:decoration-blue-400"
                                >
                                  📄 {entry.description || entry.tabName}
                                </a>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs">{entry.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingSheetTitleGroup({
                                        rawDocKey: entry.id, // using id to pass to edit handler
                                        originalSheetName: entry.tabName || '',
                                        currentCustomTitle: entry.name,
                                      });
                                      setNewCustomSheetTitleInput(entry.name);
                                    }}
                                    className="p-1 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors"
                                    title="Rename Name in Index"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveSheet(entry.id)}
                                    className="p-1 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
                                    title="Remove from Intranet"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-xs text-gray-400 max-w-xs truncate" title={entry.accessEmails.join(', ')}>
                                  {entry.accessEmails.join(', ') || 'Public'}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-wrap gap-1">
                                  {(() => {
                                    const displayTeams = (entry.team || '')
                                      .split(',')
                                      .map(t => t.trim())
                                      .filter(t => t && t.toLowerCase() !== 'unassigned' && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management');

                                    if (displayTeams.length === 0) {
                                      return <span className="text-xs text-gray-500 italic">No team assigned</span>;
                                    }

                                    return displayTeams.map((teamName, i) => (
                                      <span
                                        key={i}
                                        className={`text-xs px-2 py-0.5 rounded font-medium ${
                                          teamName === selectedTeamForManager
                                            ? 'bg-blue-600 text-white font-bold'
                                            : 'bg-blue-500/10 text-blue-400'
                                        }`}
                                      >
                                        {teamName}
                                      </span>
                                    ));
                                  })()}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <button
                                  onClick={() => toggleSheetForSelectedTeam(entry.id)}
                                  className={`px-3 py-1.5 text-xs rounded font-semibold border transition-colors ${
                                    entry.team && entry.team.includes(selectedTeamForManager)
                                      ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-600/30'
                                      : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                                  }`}
                                >
                                  {entry.team && entry.team.includes(selectedTeamForManager) ? '✓ Assigned' : '+ Assign'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      {entries.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-gray-400 italic">
                            No matching intranet sheets found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: User Roles & Homepages */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-800 p-4 rounded-xl border border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  User Role & Homepage Routing Matrix
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    {activeUserProfiles.length} Active Employees
                  </span>
                </h2>
                <p className="text-xs text-gray-400">Assign role permissions and default homepage team landing views for active employees.</p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search user email or team..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="w-full sm:w-64 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => setShowAddUserModal(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  + Add User
                </button>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <table className="w-full text-left text-sm text-gray-300">
                <thead className="bg-gray-900 text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-3">User Email</th>
                    <th className="px-6 py-3">Assigned Role</th>
                    <th className="px-6 py-3">Assigned Teams (Toggle Multiple)</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {activeUserProfiles
                    .filter(u => {
                      if (!userSearch.trim()) return true;
                      const name = getUserDisplayName({ email: u.email });
                      return matchesSearch(userSearch, {
                        primary: [name, u.email],
                        secondary: [u.team, ...(u.assignedTeams || [])]
                      });
                    })
                    .map((user) => {
                    const email = user.email;
                    const config = userMappings[email] || {};
                    const activeRoles = config.roles && config.roles.length > 0 ? config.roles : user.roles;
                    const assignedTeams = user.assignedTeams || [];

                    return (
                      <tr key={email} className="hover:bg-gray-750">
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-200">{getUserDisplayName({ email })}</span>
                            <span className="font-mono text-[11px] text-gray-400">{email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={activeRoles[0] || 'Staff'}
                            onChange={e => handleUpdateUserRole(email, e.target.value as UserRole)}
                            className="bg-gray-900 border border-gray-700 text-xs text-white rounded-lg px-2.5 py-1.5 focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="Admin">Admin</option>
                            <option value="Management">Management</option>
                            <option value="Provider">Provider</option>
                            <option value="Staff">Staff</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {allAvailableTeams.map(team => {
                              const isAssigned = assignedTeams.includes(team);
                              return (
                                <button
                                  key={team}
                                  type="button"
                                  onClick={() => handleToggleUserTeam(email, team, assignedTeams)}
                                  className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                                    isAssigned
                                      ? 'bg-blue-600/90 text-white border-blue-400 font-semibold shadow-sm'
                                      : 'bg-gray-900 text-gray-400 border-gray-750 hover:border-gray-600 hover:text-gray-200'
                                  }`}
                                  title={`Click to ${isAssigned ? 'remove' : 'assign'} ${team}`}
                                >
                                  {isAssigned ? '✓ ' : '+ '} {team}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-xs">
                          <div className="flex items-center justify-end gap-3">
                            {auth.canPreview && (
                              <Link
                                to="/"
                                onClick={() => auth.setPreviewUserEmail(email)}
                                className="px-3 py-1 bg-amber-600/20 text-amber-300 hover:bg-amber-600 hover:text-white border border-amber-500/40 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 shadow-sm"
                                title={`Preview Intranet as ${email}`}
                              >
                                👁️ Preview Site
                              </Link>
                            )}
                            <span className="text-emerald-400 font-semibold">Active</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 4: Team Directory / Team Manager */}
        {activeTab === 'team-members' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-800 p-4 rounded-xl border border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  Team Directory & Member Roster
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    {allAvailableTeams.length} Teams
                  </span>
                </h2>
                <p className="text-xs text-gray-400">Manage department teams, member roster assignments, edit team names, archive or create new teams.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search members or teams..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => setShowCreateTeamModal(true)}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 shadow-md flex-shrink-0"
                >
                  <span>➕</span> Create New Team
                </button>
              </div>
            </div>

            {/* Filter Mode Pills: Active | Archived | All Teams */}
            <div className="flex items-center gap-2 border-b border-gray-700 pb-3">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-2">Filter View:</span>
              <button
                onClick={() => setTeamTabFilter('active')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border ${
                  teamTabFilter === 'active'
                    ? 'bg-blue-600 text-white border-blue-500 shadow-md font-bold'
                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600 hover:text-white'
                }`}
              >
                Active Teams ({allAvailableTeams.filter(t => !archivedTeams.includes(t)).length})
              </button>
              <button
                onClick={() => setTeamTabFilter('archived')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border ${
                  teamTabFilter === 'archived'
                    ? 'bg-amber-600 text-white border-amber-500 shadow-md font-bold'
                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600 hover:text-white'
                }`}
              >
                📦 Archived Teams ({allAvailableTeams.filter(t => archivedTeams.includes(t)).length})
              </button>
              <button
                onClick={() => setTeamTabFilter('all')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border ${
                  teamTabFilter === 'all'
                    ? 'bg-gray-700 text-white border-gray-500 shadow-md font-bold'
                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600 hover:text-white'
                }`}
              >
                All Teams ({allAvailableTeams.length})
              </button>
            </div>

            {/* Team List Table View */}
            <div className="overflow-x-auto bg-gray-800 rounded-xl border border-gray-700 shadow-xl">
              <table className="w-full text-left text-sm text-gray-300 min-w-[850px]">
                <thead className="bg-gray-900 text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-3.5 w-64 min-w-[220px]">Team Name</th>
                    <th className="px-6 py-3.5">Active Members Roster</th>
                    <th className="px-4 py-3.5 w-28 whitespace-nowrap text-center">Status</th>
                    <th className="px-6 py-3.5 text-right w-auto whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {allAvailableTeams
                    .filter(teamName => {
                      const isArchived = archivedTeams.includes(teamName);
                      if (teamTabFilter === 'active' && isArchived) return false;
                      if (teamTabFilter === 'archived' && !isArchived) return false;

                      if (searchQuery.trim()) {
                        const q = searchQuery.toLowerCase().trim();
                        const members = teamDirectoryMap.get(teamName) || [];
                        return teamName.toLowerCase().includes(q) || members.some(m => m.email.toLowerCase().includes(q) || getUserDisplayName(m).toLowerCase().includes(q));
                      }
                      return true;
                    })
                    .map(teamName => {
                      const members = teamDirectoryMap.get(teamName) || [];
                      const isArchived = archivedTeams.includes(teamName);

                      return (
                        <tr key={teamName} className="hover:bg-gray-750 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <span className="text-xl flex-shrink-0">🏢</span>
                              <div className="flex flex-col min-w-0">
                                <button
                                  type="button"
                                  onClick={() => setSelectedTeamForManage(teamName)}
                                  className="font-bold text-white hover:text-blue-400 text-sm truncate text-left underline decoration-blue-500/40 hover:decoration-blue-400 transition-colors cursor-pointer group flex items-center gap-1.5"
                                  title={`Click to open employee roster for ${teamName}`}
                                >
                                  <span>{teamName}</span>
                                  <span className="opacity-0 group-hover:opacity-100 text-blue-400 text-xs transition-opacity">↗</span>
                                </button>
                                <span className="text-[11px] font-semibold text-blue-300">
                                  {members.length} {members.length === 1 ? 'member' : 'members'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap items-center gap-1.5 max-w-lg">
                              {members.slice(0, 4).map(m => (
                                <span key={m.email} className="text-[11px] font-mono px-2 py-0.5 bg-gray-900 text-gray-300 rounded border border-gray-700" title={m.email}>
                                  {getUserDisplayName(m)}
                                </span>
                              ))}
                              {members.length > 4 && (
                                <span className="text-[11px] px-2 py-0.5 bg-blue-900/50 text-blue-200 font-semibold rounded border border-blue-700">
                                  +{members.length - 4} more
                                </span>
                              )}
                              {members.length === 0 && (
                                <span className="text-xs text-gray-500 italic">No members assigned yet</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center">
                            {isArchived ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm">
                                📦 Archived
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-sm">
                                ✓ Active
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setSelectedTeamForManage(teamName)}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1 shadow-sm"
                                title="Manage team member roster"
                              >
                                👥 Manage Members
                              </button>
                              <button
                                onClick={() => {
                                  setTeamToRename(teamName);
                                  setNewRenameTeamInput(teamName);
                                }}
                                className="px-2.5 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs font-semibold rounded-lg transition-colors"
                                title="Edit team name"
                              >
                                ✏️ Rename
                              </button>
                              <button
                                onClick={() => handleToggleArchiveTeam(teamName)}
                                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors border ${
                                  isArchived
                                    ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-600 hover:text-white'
                                    : 'bg-amber-600/20 text-amber-300 border-amber-500/40 hover:bg-amber-600 hover:text-white'
                                }`}
                                title={isArchived ? 'Unarchive team' : 'Archive team'}
                              >
                                {isArchived ? 'Unarchive' : 'Archive'}
                              </button>
                              <button
                                onClick={() => handleDeleteTeam(teamName)}
                                className="px-2.5 py-1.5 bg-red-600/20 text-red-400 hover:bg-red-600 hover:text-white border border-red-500/30 rounded-lg text-xs font-semibold transition-colors"
                                title="Delete team"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 4: Printable Document Groups */}
        {activeTab === 'printable-docs' && (
          <div className="space-y-6">
            {/* Header Control Card */}
            <div className="bg-gray-800 p-5 rounded-2xl border border-gray-700 space-y-4 shadow-xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-700 pb-4">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <span className="p-2 bg-blue-500/20 text-blue-400 rounded-xl">📄</span>
                    Printable Document Groups Manager
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    Create custom document packets/groups, assign printable PDF forms, and configure their exact display order for end users.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setNewGroupNameInput('');
                      setShowCreateGroupModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-blue-500/20 transition-all cursor-pointer"
                  >
                    <span>+</span> Create New Group
                  </button>
                </div>
              </div>

              {/* Filter Pills & Group Search */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => setDocGroupTabFilter('active')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      docGroupTabFilter === 'active'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-900 text-gray-400 hover:text-white border border-gray-700'
                    }`}
                  >
                    Active ({docGroups.filter(g => !g.archived).length})
                  </button>
                  <button
                    onClick={() => setDocGroupTabFilter('archived')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      docGroupTabFilter === 'archived'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-900 text-gray-400 hover:text-white border border-gray-700'
                    }`}
                  >
                    Archived ({docGroups.filter(g => g.archived).length})
                  </button>
                  <button
                    onClick={() => setDocGroupTabFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      docGroupTabFilter === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-900 text-gray-400 hover:text-white border border-gray-700'
                    }`}
                  >
                    All ({docGroups.length})
                  </button>
                </div>

                <div className="relative w-full sm:w-72">
                  <input
                    type="text"
                    placeholder="Search document groups..."
                    value={docGroupSearch}
                    onChange={e => setDocGroupSearch(e.target.value)}
                    className="w-full pl-8 pr-4 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <span className="absolute left-2.5 top-2 text-gray-500 text-xs">🔍</span>
                </div>
              </div>
            </div>

            {/* Main Content Grid: Groups Sidebar (Left) + Group Editor (Right) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Groups List (Left) */}
              <div className="lg:col-span-4 space-y-3">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-extrabold uppercase tracking-wider text-gray-400">
                      Document Groups ({filteredDocGroups.length})
                    </h3>
                  </div>

                  <div className="space-y-2 max-h-[700px] overflow-y-auto pr-1">
                    {filteredDocGroups.map(group => {
                      const isSelected = activeSelectedGroup?.id === group.id;
                      const count = (group.documentIds || []).length;
                      return (
                        <div
                          key={group.id}
                          onClick={() => setSelectedDocGroupId(group.id)}
                          className={`p-3 rounded-xl border transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-blue-900/30 border-blue-500 shadow-md ring-1 ring-blue-500/50'
                              : 'bg-gray-900/90 border-gray-700/80 hover:border-gray-600 hover:bg-gray-900'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-white truncate">{group.name}</span>
                                {group.archived && (
                                  <span className="text-[10px] px-1.5 py-0.2 bg-amber-950/80 border border-amber-500/50 text-amber-300 rounded font-semibold">
                                    Archived
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5">
                                <span className="font-semibold text-blue-400">{count}</span>
                                <span>{count === 1 ? 'document assigned' : 'documents assigned'}</span>
                              </p>
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                              <button
                                onClick={() => {
                                  setGroupToRename(group);
                                  setRenameGroupInput(group.name);
                                }}
                                className="p-1.5 text-gray-400 hover:text-blue-300 hover:bg-gray-800 rounded transition-colors text-xs"
                                title="Rename Group"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleToggleArchiveGroup(group)}
                                className={`p-1.5 rounded transition-colors text-xs ${
                                  group.archived
                                    ? 'text-emerald-400 hover:text-emerald-300 hover:bg-gray-800'
                                    : 'text-amber-400 hover:text-amber-300 hover:bg-gray-800'
                                }`}
                                title={group.archived ? 'Unarchive Group' : 'Archive Group'}
                              >
                                {group.archived ? '↺' : '📦'}
                              </button>
                              <button
                                onClick={() => handleDeleteGroup(group)}
                                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded transition-colors text-xs"
                                title="Delete Group"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {filteredDocGroups.length === 0 && (
                      <div className="p-6 text-center text-xs text-gray-500 border border-dashed border-gray-700 rounded-xl">
                        No document groups found. Click &quot;+ Create New Group&quot; to add one.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Group Editor: Assigned Docs (Ordered) & Forms Library (Right) */}
              <div className="lg:col-span-8 space-y-4">
                {!activeSelectedGroup ? (
                  <div className="bg-gray-800 p-12 rounded-xl border border-gray-700 text-center space-y-3">
                    <span className="text-4xl">📂</span>
                    <h3 className="text-base font-bold text-white">No Group Selected</h3>
                    <p className="text-xs text-gray-400 max-w-md mx-auto">
                      Select a printable document group on the left or create a new one to assign forms and set their custom display order.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Active Group Header Banner */}
                    <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 shadow-lg">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-base font-extrabold text-white flex items-center gap-2">
                            <span>📂</span>
                            <span>{activeSelectedGroup.name}</span>
                          </h3>
                          {activeSelectedGroup.archived ? (
                            <span className="text-xs px-2 py-0.5 bg-amber-950/80 border border-amber-500/50 text-amber-300 rounded-full font-semibold">
                              Archived
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 bg-emerald-950/80 border border-emerald-500/50 text-emerald-300 rounded-full font-semibold">
                              Active Group
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {assignedDocsInSelectedGroup.length} forms assigned • Custom display sequence configured below
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setGroupToRename(activeSelectedGroup);
                            setRenameGroupInput(activeSelectedGroup.name);
                          }}
                          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                        >
                          <span>✏️</span> Rename
                        </button>
                        <button
                          onClick={() => handleToggleArchiveGroup(activeSelectedGroup)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border ${
                            activeSelectedGroup.archived
                              ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-600 hover:text-white'
                              : 'bg-amber-600/20 text-amber-300 border-amber-500/40 hover:bg-amber-600 hover:text-white'
                          }`}
                        >
                          <span>{activeSelectedGroup.archived ? '↺' : '📦'}</span>
                          <span>{activeSelectedGroup.archived ? 'Unarchive' : 'Archive'}</span>
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(activeSelectedGroup)}
                          className="px-3 py-1.5 bg-red-600/20 text-red-300 hover:bg-red-600 hover:text-white border border-red-500/30 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                        >
                          <span>🗑️</span> Delete
                        </button>
                      </div>
                    </div>

                    {/* Split Panes: Assigned Forms (Left) & Printable Forms Library (Right) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Sub-Pane 1: Assigned Documents with Custom Ordering */}
                      <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 space-y-3 flex flex-col">
                        <div className="flex items-center justify-between border-b border-gray-700 pb-2.5">
                          <div>
                            <h4 className="text-xs font-extrabold text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                              <span>✓</span> Assigned Forms ({assignedDocsInSelectedGroup.length})
                            </h4>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              Drag or click ▲ / ▼ to reorder.
                            </p>
                          </div>
                          {assignedDocsInSelectedGroup.length > 0 && (
                            <button
                              onClick={() => {
                                if (window.confirm('Clear all documents from this group?')) {
                                  const updated = { ...activeSelectedGroup, documentIds: [], updatedAt: Date.now() };
                                  setDocGroups(docGroups.map(g => g.id === activeSelectedGroup.id ? updated : g));
                                  savePrintableDocGroup(updated);
                                }
                              }}
                              className="text-[10px] text-red-400 hover:text-red-300 font-semibold underline"
                            >
                              Clear All
                            </button>
                          )}
                        </div>

                        <div className="space-y-1.5 max-h-[580px] overflow-y-auto pr-1 flex-1">
                          {assignedDocsInSelectedGroup.map((doc, idx) => {
                            const isDragging = draggedGroupDocId === doc.id;
                            const isDragOver = dragOverGroupDocId === doc.id;
                            return (
                              <div
                                key={doc.id}
                                draggable
                                onDragStart={e => {
                                  e.dataTransfer.setData('text/plain', doc.id);
                                  setDraggedGroupDocId(doc.id);
                                }}
                                onDragOver={e => {
                                  e.preventDefault();
                                  setDragOverGroupDocId(doc.id);
                                }}
                                onDragLeave={() => {
                                  if (dragOverGroupDocId === doc.id) setDragOverGroupDocId(null);
                                }}
                                onDrop={e => {
                                  e.preventDefault();
                                  const sourceId = e.dataTransfer.getData('text/plain') || draggedGroupDocId;
                                  if (sourceId) {
                                    handleDropDocInGroup(sourceId, doc.id);
                                  }
                                }}
                                onDragEnd={() => {
                                  setDraggedGroupDocId(null);
                                  setDragOverGroupDocId(null);
                                }}
                                className={`p-2.5 rounded-lg border transition-all flex items-center justify-between gap-2 ${
                                  isDragging
                                    ? 'opacity-40 border-dashed border-blue-400 bg-blue-950/40'
                                    : isDragOver
                                    ? 'border-blue-400 bg-blue-900/40'
                                    : 'bg-gray-900/90 border-gray-700 hover:border-gray-600'
                                }`}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  {/* Drag Handle */}
                                  <span
                                    className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300 text-sm select-none px-0.5"
                                    title="Drag to reorder"
                                  >
                                    ⠿
                                  </span>

                                  {/* Order Badge */}
                                  <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-blue-900/60 text-blue-300 border border-blue-700/50 flex-shrink-0">
                                    #{idx + 1}
                                  </span>

                                  {/* Title & Info */}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-bold text-white truncate" title={doc.fullName || doc.title}>
                                      {doc.title}
                                    </p>
                                    <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                      <span className="text-gray-500">#{doc.docNumber}</span>
                                      <span className="px-1.5 py-0.2 bg-gray-800 text-gray-300 rounded font-medium">
                                        {doc.category}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Actions: Move Up / Down & Remove */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => handleMoveDocInGroup(idx, 'up')}
                                    disabled={idx === 0}
                                    className="p-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-20 disabled:hover:bg-gray-800 text-xs cursor-pointer"
                                    title="Move Up"
                                  >
                                    ▲
                                  </button>
                                  <button
                                    onClick={() => handleMoveDocInGroup(idx, 'down')}
                                    disabled={idx === assignedDocsInSelectedGroup.length - 1}
                                    className="p-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-20 disabled:hover:bg-gray-800 text-xs cursor-pointer"
                                    title="Move Down"
                                  >
                                    ▼
                                  </button>
                                  <button
                                    onClick={() => handleRemoveDocFromGroup(doc.id)}
                                    className="p-1 rounded bg-red-900/30 hover:bg-red-800/60 text-red-400 hover:text-red-200 text-xs font-bold cursor-pointer transition-colors ml-1"
                                    title="Remove from group"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            );
                          })}

                          {assignedDocsInSelectedGroup.length === 0 && (
                            <div className="p-8 text-center text-xs text-gray-500 border border-dashed border-gray-700 rounded-xl space-y-1">
                              <p className="font-semibold text-gray-400">No documents in this group yet.</p>
                              <p className="text-[11px]">Click &quot;+ Add&quot; on any form in the Library on the right.</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Sub-Pane 2: Printable Forms Library */}
                      <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 space-y-3 flex flex-col">
                        <div className="flex items-center justify-between border-b border-gray-700 pb-2.5">
                          <div>
                            <h4 className="text-xs font-extrabold text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                              <span>📄</span> Printable Forms Library ({filteredLibraryDocs.length})
                            </h4>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              {ALL_PRINTABLE_DOCS.length} total clinic forms available.
                            </p>
                          </div>
                          {filteredLibraryDocs.some(d => !activeSelectedGroup.documentIds.some(id => (LEGACY_DOC_ID_ALIASES[id] || id) === (LEGACY_DOC_ID_ALIASES[d.id] || d.id) || id === d.id)) && (
                            <button
                              onClick={handleAddAllFilteredDocs}
                              className="text-[10px] px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded transition-colors shadow-sm"
                            >
                              + Add All Filtered
                            </button>
                          )}
                        </div>

                        {/* Search & Category Filter */}
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              placeholder="Search forms by name/number..."
                              value={docLibrarySearch}
                              onChange={e => setDocLibrarySearch(e.target.value)}
                              className="w-full pl-7 pr-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <span className="absolute left-2 top-2 text-gray-500 text-xs">🔍</span>
                          </div>

                          <select
                            value={docLibraryCategory}
                            onChange={e => setDocLibraryCategory(e.target.value)}
                            className="bg-gray-900 border border-gray-700 rounded-lg text-xs text-white px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            {docCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>

                        {/* Library Docs List */}
                        <div className="space-y-1.5 max-h-[520px] overflow-y-auto pr-1 flex-1">
                          {filteredLibraryDocs.map(doc => {
                            const isAssigned = activeSelectedGroup.documentIds.some(id => (LEGACY_DOC_ID_ALIASES[id] || id) === (LEGACY_DOC_ID_ALIASES[doc.id] || doc.id) || id === doc.id);
                            return (
                              <div
                                key={doc.id}
                                className={`p-2.5 rounded-lg border transition-all flex items-center justify-between gap-2 ${
                                  isAssigned
                                    ? 'bg-blue-950/20 border-blue-800/40'
                                    : 'bg-gray-900/90 border-gray-700 hover:border-gray-600'
                                }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-bold text-white truncate" title={doc.fullName || doc.title}>
                                    {doc.title}
                                  </p>
                                  <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
                                    <span className="text-gray-500">#{doc.docNumber}</span>
                                    <span className="px-1.5 py-0.2 bg-gray-800 text-gray-300 rounded font-medium">
                                      {doc.category}
                                    </span>
                                    {doc.pdfUrl && (
                                      <a
                                        href={doc.pdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:underline flex items-center gap-0.5"
                                      >
                                        PDF ↗
                                      </a>
                                    )}
                                  </div>
                                </div>

                                <div className="flex-shrink-0">
                                  {isAssigned ? (
                                    <button
                                      onClick={() => handleRemoveDocFromGroup(doc.id)}
                                      className="px-2.5 py-1 rounded bg-blue-600/30 hover:bg-red-900/40 text-blue-300 hover:text-red-300 border border-blue-500/40 hover:border-red-500/40 text-[11px] font-bold transition-colors cursor-pointer"
                                      title="Click to remove from group"
                                    >
                                      ✓ In Group
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleAddDocToGroup(doc.id)}
                                      className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold transition-colors cursor-pointer shadow-sm"
                                    >
                                      + Add
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          {filteredLibraryDocs.length === 0 && (
                            <div className="p-8 text-center text-xs text-gray-500 border border-dashed border-gray-700 rounded-xl">
                              No printable forms match your search filter.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: Usage & Analytics */}
        {activeTab === 'usage' && (
          <UsageAnalyticsTab isSuperAdmin={isAuthorized} />
        )}

        {/* TAB 6: System Diagnostics */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 space-y-4">
              <h2 className="text-lg font-bold text-white">System Diagnostics & Cache</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <p className="text-xs text-gray-400">Google Sheets Connectivity</p>
                  <p className="text-sm font-bold text-emerald-400 mt-1">
                    {healthStatus?.spreadsheetConnected !== false ? '✓ Connected' : '❌ Disconnected'}
                  </p>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <p className="text-xs text-gray-400">API Proxy Status</p>
                  <p className="text-sm font-bold text-emerald-400 mt-1">
                    {healthStatus?.status ? `✓ ${healthStatus.status}` : '✓ Active (200 OK)'}
                  </p>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <p className="text-xs text-gray-400">Local Cache</p>
                  <button
                    onClick={() => {
                      clearCache();
                      alert('Local API cache invalidated successfully!');
                    }}
                    className="mt-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded"
                  >
                    Clear Cache Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* MODAL 1: Edit Resource Access Modal */}
      {editingResource && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-xl w-full p-6 space-y-6 shadow-2xl">
            <div className="flex justify-between items-start border-b border-gray-700 pb-4">
              <div>
                <h3 className="text-lg font-bold text-white">{editingResource.name}</h3>
                <p className="text-xs text-gray-400">{editingResource.description || 'Google Sheet Resource'}</p>
              </div>
              <button onClick={() => setEditingResource(null)} className="text-gray-400 hover:text-white text-lg font-bold">✕</button>
            </div>

            {/* Select Teams Section */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-300 uppercase tracking-wider">Assigned Teams (Click to toggle)</label>
              <div className="flex flex-wrap gap-2">
                {allAvailableTeams.map(team => {
                  const isSelected = selectedTeams.includes(team);
                  return (
                    <button
                      key={team}
                      type="button"
                      onClick={() => toggleTeamSelection(team)}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-500 font-semibold'
                          : 'bg-gray-900 text-gray-400 border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      {isSelected ? '✓ ' : '+ '} {team}
                    </button>
                  );
                })}
              </div>

              {/* Add Custom Team */}
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  placeholder="Or type custom team name..."
                  value={newTeamInput}
                  onChange={e => setNewTeamInput(e.target.value)}
                  className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddCustomTeam}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-semibold rounded-lg"
                >
                  Add Team
                </button>
              </div>
            </div>

            {/* Select Emails Section */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-300 uppercase tracking-wider">
                User Email Permissions ({selectedEmails.length} assigned) - Click pill to toggle off
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-2 bg-gray-900 rounded-lg border border-gray-700">
                {selectedEmails.map(email => {
                  const isTerminated = TERMINATED_EMPLOYEE_EMAILS.has(email.toLowerCase().trim());
                  return (
                    <button
                      key={email}
                      type="button"
                      onClick={() => toggleEmailSelection(email)}
                      className={`text-[11px] font-mono px-2.5 py-1 rounded transition-colors flex items-center gap-1.5 group ${
                        isTerminated
                          ? 'bg-amber-700/90 text-amber-100 font-bold border border-amber-500 hover:bg-red-600 hover:text-white'
                          : 'bg-emerald-600 text-white font-semibold hover:bg-red-600'
                      }`}
                      title={isTerminated ? 'Terminated Employee Account - Click to remove access' : 'Click to remove email access permission'}
                    >
                      <span>✓ {email}</span>
                      {isTerminated && <span className="text-[9px] bg-amber-950 text-amber-200 px-1 rounded font-sans uppercase">Terminated</span>}
                      <span className="text-emerald-200 group-hover:text-white font-bold">✕</span>
                    </button>
                  );
                })}

                {selectedEmails.length === 0 && (
                  <p className="text-xs text-gray-500 italic p-1">No explicit user emails assigned yet. All users in assigned teams have access.</p>
                )}
              </div>

              {/* Add Custom Email with Combobox Autocomplete */}
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-1">
                  <EmailComboboxInput
                    value={newEmailInput}
                    onChange={setNewEmailInput}
                    placeholder="Search name or type email (e.g. automation@ssdspc.com)..."
                    userProfiles={activeUserProfiles}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddCustomEmail}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl flex-shrink-0 transition-colors"
                >
                  Add Email
                </button>
              </div>
            </div>

            {/* Save Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setEditingResource(null)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveResourceAccess}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full p-6 space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white">Add New User Assignment</h3>

            <div>
              <label className="text-xs text-gray-400">User Email Address (Autocomplete enabled)</label>
              <input
                type="email"
                list="all-user-emails-list"
                placeholder="e.g. employee@ssdspc.com"
                value={newUserEmail}
                onChange={e => setNewUserEmail(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="text-xs text-gray-400">Assigned Role</label>
              <select
                value={newUserRole}
                onChange={e => setNewUserRole(e.target.value as UserRole)}
                className="w-full mt-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="Admin">Admin</option>
                <option value="Management">Management</option>
                <option value="Provider">Provider</option>
                <option value="Staff">Staff</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-400">Default Homepage Team</label>
              <select
                value={newUserTeam}
                onChange={e => setNewUserTeam(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {allAvailableTeams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setShowAddUserModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddUserMapping}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg"
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL: Manage Team Members Roster (Checkboxed Employee List) */}
      {selectedTeamForManage && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-2xl w-full p-6 space-y-4 shadow-2xl flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="flex justify-between items-start border-b border-gray-700 pb-3 flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <span>🏢</span> {selectedTeamForManage} Roster
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    {(teamDirectoryMap.get(selectedTeamForManage) || []).length} assigned members
                  </span>
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  Check or uncheck employees below to manage team membership for <strong className="text-white">{selectedTeamForManage}</strong>.
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedTeamForManage(null);
                  setRosterSearchQuery('');
                }}
                className="text-gray-400 hover:text-white text-lg font-bold p-1"
              >
                ✕
              </button>
            </div>

            {/* Search Filter Bar */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="🔍 Search employees by name or email..."
                  value={rosterSearchQuery}
                  onChange={e => setRosterSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 bg-gray-900 border border-gray-700 rounded-xl text-xs text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">🔍</span>
                {rosterSearchQuery && (
                  <button
                    onClick={() => setRosterSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable Employee Roster Lists */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {(() => {
                const q = rosterSearchQuery.trim();
                const filteredEmployees = activeUserProfiles.filter(u => {
                  if (!q) return true;
                  const name = getUserDisplayName(u);
                  return matchesSearch(q, {
                    primary: [name, u.email],
                    secondary: u.team
                  });
                });

                const assignedList = filteredEmployees.filter(u => (u.assignedTeams || []).includes(selectedTeamForManage!));
                const availableList = filteredEmployees.filter(u => !(u.assignedTeams || []).includes(selectedTeamForManage!));

                return (
                  <>
                    {/* SECTION 1: ASSIGNED TEAM MEMBERS (CHECKED) */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-extrabold text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                          <span>✓</span> Assigned Members ({assignedList.length})
                        </label>
                        <span className="text-[11px] text-gray-400">Checked = Part of Team</span>
                      </div>

                      <div className="divide-y divide-gray-750 bg-blue-950/20 rounded-xl border border-blue-600/40 p-1.5 space-y-1">
                        {assignedList.map(u => {
                          const name = getUserDisplayName(u);
                          return (
                            <label
                              key={u.email}
                              className="pt-2 first:pt-0 flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-blue-900/30 cursor-pointer transition-colors group"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  onChange={() => handleToggleUserTeam(u.email, selectedTeamForManage!, u.assignedTeams)}
                                  className="w-4 h-4 rounded text-blue-600 bg-gray-900 border-blue-500 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-bold text-white group-hover:text-blue-200 truncate">{name}</p>
                                  <p className="text-[11px] font-mono text-blue-300/80 truncate">{u.email}</p>
                                </div>
                              </div>
                              <span className="text-[10px] px-2 py-0.5 rounded font-extrabold bg-blue-600 text-white uppercase flex-shrink-0">
                                Member
                              </span>
                            </label>
                          );
                        })}

                        {assignedList.length === 0 && (
                          <p className="text-xs text-gray-500 italic p-3 text-center">
                            {q ? 'No assigned members match your search.' : 'No members assigned to this team yet. Check any employee below to add them.'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* SECTION 2: OTHER EMPLOYEES (UNCHECKED - CLICK TO ADD) */}
                    <div className="space-y-1.5 pt-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-extrabold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <span>+</span> Other Employees - Click Checkbox to Add ({availableList.length})
                        </label>
                        <span className="text-[11px] text-gray-500">Unchecked = Not in Team</span>
                      </div>

                      <div className="divide-y divide-gray-750 bg-gray-900 rounded-xl border border-gray-700 p-1.5 space-y-1">
                        {availableList.map(u => {
                          const name = getUserDisplayName(u);
                          return (
                            <label
                              key={u.email}
                              className="pt-2 first:pt-0 flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-800 cursor-pointer transition-colors group"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() => handleToggleUserTeam(u.email, selectedTeamForManage!, u.assignedTeams)}
                                  className="w-4 h-4 rounded text-blue-600 bg-gray-800 border-gray-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-bold text-gray-200 group-hover:text-white truncate">{name}</p>
                                  <p className="text-[11px] font-mono text-gray-400 truncate">{u.email}</p>
                                </div>
                              </div>
                              <span className="text-[10px] px-2 py-0.5 rounded font-medium bg-gray-800 text-gray-400 flex-shrink-0">
                                Available
                              </span>
                            </label>
                          );
                        })}

                        {availableList.length === 0 && (
                          <p className="text-xs text-gray-500 italic p-3 text-center">
                            {q ? 'No available employees match your search.' : 'All active employees are currently assigned to this team.'}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center pt-3 border-t border-gray-700 flex-shrink-0">
              <span className="text-xs text-gray-400">
                Changes are saved automatically.
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedTeamForManage(null);
                  setRosterSearchQuery('');
                }}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition-colors shadow-md"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Create New Team */}
      {showCreateTeamModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full p-6 space-y-6 shadow-2xl">
            <div className="flex justify-between items-start border-b border-gray-700 pb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><span>➕</span> Create New Team</h3>
              <button onClick={() => setShowCreateTeamModal(false)} className="text-gray-400 hover:text-white text-lg font-bold">✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-2">Team Name</label>
                <input
                  type="text"
                  placeholder="e.g., Surgery MA, Remote Support, Billing Admins..."
                  value={newCreateTeamName}
                  onChange={e => setNewCreateTeamName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-2">Assign Initial Members (Optional)</label>
                <div className="max-h-40 overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg p-2 space-y-1">
                  {activeUserProfiles.map(u => {
                    const isSelected = newCreateTeamMembers.includes(u.email);
                    return (
                      <button
                        key={u.email}
                        type="button"
                        onClick={() => {
                          setNewCreateTeamMembers(prev =>
                            prev.includes(u.email) ? prev.filter(e => e !== u.email) : [...prev, u.email]
                          );
                        }}
                        className={`w-full text-left px-2.5 py-1.5 rounded text-xs font-mono transition-colors flex items-center justify-between ${
                          isSelected ? 'bg-blue-600 text-white font-bold' : 'text-gray-300 hover:bg-gray-800'
                        }`}
                      >
                        <span>{u.email}</span>
                        {isSelected && <span>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setShowCreateTeamModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateTeam}
                disabled={!newCreateTeamName.trim()}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-colors"
              >
                Create Team
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Rename Team */}
      {teamToRename && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full p-6 space-y-6 shadow-2xl">
            <div className="flex justify-between items-start border-b border-gray-700 pb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><span>✏️</span> Rename Team</h3>
              <button onClick={() => setTeamToRename(null)} className="text-gray-400 hover:text-white text-lg font-bold">✕</button>
            </div>

            <div className="space-y-3">
              <p className="text-xs text-gray-400">Current Name: <strong className="text-white font-mono">{teamToRename}</strong></p>
              <div>
                <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-2">New Team Name</label>
                <input
                  type="text"
                  value={newRenameTeamInput}
                  onChange={e => setNewRenameTeamInput(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setTeamToRename(null)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRenameTeam}
                disabled={!newRenameTeamInput.trim()}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-colors"
              >
                Save Name
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL: Rename Sheet Display Title */}
      {editingSheetTitleGroup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-lg w-full p-6 space-y-6 shadow-2xl">
            <div className="flex justify-between items-start border-b border-gray-700 pb-4">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <span>✏️</span> Rename Spreadsheet Display Title
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  Customize how this spreadsheet appears across the intranet. The real Google Sheet file name remains unchanged.
                </p>
              </div>
              <button
                onClick={() => setEditingSheetTitleGroup(null)}
                className="text-gray-400 hover:text-white text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Original Google Sheet File Name (Real Name)
                </label>
                <div className="p-2.5 bg-gray-900 border border-gray-700/80 rounded-lg text-xs font-mono text-gray-300 break-all">
                  {editingSheetTitleGroup.originalSheetName}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-amber-300 uppercase tracking-wider mb-1">
                  Intranet Display Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. Internal Billing Errors Log"
                  value={newCustomSheetTitleInput}
                  onChange={e => setNewCustomSheetTitleInput(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-900 border border-amber-500/50 focus:border-amber-400 rounded-xl text-sm font-semibold text-white focus:ring-2 focus:ring-amber-500/40 outline-none"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  This title will be shown on all tabs, cards, and navigation on the intranet portal.
                </p>
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={() => handleResetSheetRename()}
                className="px-3.5 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                title="Remove custom title and restore original Google Sheet name"
              >
                Reset to Original Name
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditingSheetTitleGroup(null)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveSheetRename(editingSheetTitleGroup.rawDocKey, newCustomSheetTitleInput)}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors shadow-md cursor-pointer"
                >
                  Save Display Title
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Create Printable Document Group */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full p-6 space-y-5 shadow-2xl">
            <div className="flex justify-between items-start border-b border-gray-700 pb-3">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <span>📄</span> Create Printable Document Group
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Group printable forms together for specific clinic workflows (e.g. Front Desk, Mohs Surgery).
                </p>
              </div>
              <button
                onClick={() => setShowCreateGroupModal(false)}
                className="text-gray-400 hover:text-white text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-1.5">
                Group Name
              </label>
              <input
                type="text"
                autoFocus
                placeholder="e.g. Front Desk Patient Packets"
                value={newGroupNameInput}
                onChange={e => setNewGroupNameInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreateDocGroup();
                }}
                className="w-full px-3.5 py-2.5 bg-gray-900 border border-gray-700 focus:border-blue-500 rounded-xl text-sm font-semibold text-white focus:ring-2 focus:ring-blue-500/40 outline-none"
              />
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setShowCreateGroupModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateDocGroup}
                disabled={!newGroupNameInput.trim()}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors shadow-md disabled:opacity-40 cursor-pointer"
              >
                Create Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Rename Printable Document Group */}
      {groupToRename && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full p-6 space-y-5 shadow-2xl">
            <div className="flex justify-between items-start border-b border-gray-700 pb-3">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <span>✏️</span> Rename Document Group
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Update the group name displayed on the main dashboard.
                </p>
              </div>
              <button
                onClick={() => setGroupToRename(null)}
                className="text-gray-400 hover:text-white text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Current Name
                </label>
                <div className="p-2.5 bg-gray-900 border border-gray-700/80 rounded-lg text-xs font-mono text-gray-300">
                  {groupToRename.name}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-blue-400 uppercase tracking-wider mb-1.5">
                  New Group Name
                </label>
                <input
                  type="text"
                  autoFocus
                  value={renameGroupInput}
                  onChange={e => setRenameGroupInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveRenameGroup();
                  }}
                  className="w-full px-3.5 py-2.5 bg-gray-900 border border-blue-500/50 focus:border-blue-400 rounded-xl text-sm font-semibold text-white focus:ring-2 focus:ring-blue-500/40 outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setGroupToRename(null)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRenameGroup}
                disabled={!renameGroupInput.trim()}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors shadow-md disabled:opacity-40 cursor-pointer"
              >
                Save Name
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Page Footer */}
      <footer className="py-6 text-center text-xs text-gray-500 border-t border-gray-800 mt-12">
        <p className="flex items-center justify-center gap-2">
          <span>Southern Skies Dermatology & Surgery Intranet Control Panel</span>
          <span>•</span>
          <span className="font-mono font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/30">
            {APP_VERSION}
          </span>
        </p>
      </footer>
    </div>
  );
}
